import { Button, FormControl, FormGroup, MenuItem, Select, Stack, Typography } from "@mui/material"
import { Link } from "../../components/global/Link"
import { PlusIcon } from "../../assets/svgs/PlusIcon"
import { useEffect, useRef, useState } from "react"
import { UploadTagSuccessModal } from "./UploadTagSuccessModal"
import { UploadTagErrorModal } from "./UploadTagErrorModal"
import { useAPI } from "../../hooks/useAPI"
import { ClientData } from "../../types"
import { useAuth } from "../../assets/contexts/AuthContext"

interface UploadTagPanelProps {
  refetchTagUploads: () => void;
}

export const UploadTagPanel = ({ refetchTagUploads }: UploadTagPanelProps) => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalErrorFileUrl, setModalErrorFileUrl] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const { fetchData } = useAPI();
  const [clients, setClients] = useState<ClientData[]>([]);
  const acceptedFileTypes = ['.csv', '.txt'];
  const [client, setClient] = useState<ClientData | null>(null);

  const refresh = async () => {
    setFiles([]);
    setClient(null);
    refetchTagUploads();
  }

  const refetchClients = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const {errors, data} = await fetchData('/clients/all');
    setClients(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  }

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchClients();
    }
  }, [isAuthenticated]);

  const onShowSuccess = (title: string, message: string) => {
    setModalTitle(title);
    setModalMessage(message);
    setShowSuccess(true);
    setShowError(false);
    refresh();
  }

  const onShowError = (title: string, message: string, errorFileUrl: string) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalErrorFileUrl(errorFileUrl);
    setShowSuccess(false);
    setShowError(true);
    refresh();
  }

  const onClodeModal = () => {
    setShowSuccess(false);
    setShowError(false);
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles);
      const uniqueFiles = newFiles.filter(file => !files.some(existingFile => existingFile.name === file.name));
      const updatedFiles = [...files, ...uniqueFiles];
      setFiles(updatedFiles);
    }
  }

  const handleAddFiles = async (event: any) => {
    event?.preventDefault && event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append('client_id', client?.id ?? '');
    files.forEach(file => {
      formData.append('files[]', file);
    });
    const { data, errors } = await fetchData('/tags/upload', formData, 'POST');
    if (data?.success) {
      onShowSuccess('Upload Successful', data.message);
    } else {
      onShowError('Error Uploading', data?.message ?? '<p>An error occurred during uploading tags.</p>', data?.error_log_file ?? '');
    }
  };

  const onDeactivateTags = async(event: any) => {
    event?.preventDefault && event.preventDefault();
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file);
    });
    const { data, errors } = await fetchData('/tags/deactivate', formData, 'POST');
    if (data?.success) {
      onShowSuccess('Deactivate Successful', data.message);
    } else {
      onShowError('Error Deactivating', '<p>An error occurred during deactivating tags.</p>', data?.error_log_file ?? '');
    }
  }

  const isFileAccepted = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return fileExtension ? acceptedFileTypes.includes(`.${fileExtension}`) : false;
  };

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const selectedFiles = event.dataTransfer.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles).filter(file => isFileAccepted(file));
      const uniqueFiles = newFiles.filter(file => !files.some(existingFile => existingFile.name === file.name));
      const updatedFiles = [...files, ...uniqueFiles];
      setFiles(updatedFiles);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const onActivateTags = async(event: any) => {
    event?.preventDefault && event.preventDefault();
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file);
    });
    const { data, errors } = await fetchData('/tags/activate', formData, 'POST');
    if (data?.success) {
      onShowSuccess('Activate Successful', data.message);
    } else {
      onShowError('Error Activating', '<p>An error occurred during activating tags.</p>', data?.error_log_file ?? '');
    }
  }

  return (
    <form className="puload-tag-panel" onSubmit={handleSubmit}>
      <Stack
        className="puload-tag-panel-inner"
        spacing={2}
        sx={{
          borderRadius: 2,
          border: '1px solid #DCDCE0',
          boxShadow: '0px 2px 4px 0px #00000040',
          padding: 4,
        }}
      >
        <UploadTagSuccessModal open={showSuccess} handleClose={onClodeModal} title={modalTitle} messsage={modalMessage} />
        <UploadTagErrorModal open={showError} handleClose={onClodeModal} title={modalTitle} messsage={modalMessage} errorFileUrl={modalErrorFileUrl} />
        <Stack>
          <Typography fontWeight="600">Drag the files containing your tags and drop them on the upload box below.</Typography>
          <Typography fontWeight="600">Click <Link to="#" onClick={handleAddFiles}><span>here</span></Link> to manually upload, If you can’t drag and drop.</Typography>
          <Typography fontWeight="600">Format: Single column UUID with NO header row.</Typography>
        </Stack>
        <Stack direction='row' spacing={4}>
          <input 
            type="file" 
            id="upload_file" 
            name="upload_file" 
            accept={acceptedFileTypes.join(',')}
            style={{ display: 'none' }} 
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          <Button 
            id="file_upload_button" 
            sx={{
              width: "50%",
              borderRadius: 1,
              border: `1px solid ${isDragOver ? '#009444' : '#DCDCE0'} `,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              spacing: 1,
              cursor: "pointer",
              margin: "0px !important",
              height: "112px",
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleAddFiles}
          >
            <PlusIcon color="#4B5851" />
            <Typography>Upload Files</Typography>
          </Button>
          <Stack width="50%" spacing={1}>
            <Typography>You have selected <span style={{fontWeight: '600'}}>{files.length}</span> files to upload.</Typography>
            <Typography variant="body2" fontStyle="italic">
              {
                files.map((item: File, index: number) => (
                  <span key={index}>
                    <span>{item.name}</span><br />
                  </span>
                ))
              }
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <FormGroup>
            <FormControl style={{ width: "calc(50% - 16px)" }}>
              <Typography fontWeight="600">Client</Typography>
              <Select
                variant="outlined"
                value={client?.id ?? ''}
                onChange={(e) => setClient(clients.find((item) => item.id = e.target.value) ?? null)}
                sx={{ width: '100%' }}
              >
                {
                  clients.map((item: any, index: number) => (
                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </FormGroup>
        </Stack>
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button variant="contained" size="large" type="submit" disabled={files.length == 0 || client == null}>
            Upload Tags
          </Button >
          <Button variant="contained" size="large" type="submit" onClick={onActivateTags} disabled={files.length == 0}>
            Activate Tags
          </Button >
          <Button variant="contained" color="error" size="large" type="button" onClick={onDeactivateTags} disabled={files.length == 0}>
            Deactivate Tags
          </Button >
        </Stack>
      </Stack>
    </form>
  )
}